/** The registration form: locked email, first + last name, the optional
 * marketing-consent panel, and the create CTA.
 *
 * One form, two entrypoints. The OTP flow reaches it after a verified code
 * (signin/flow.tsx's `name` step); a new Facebook/Google user reaches it after
 * the provider hop (entries/complete-registration.tsx). Everything the two
 * screens shared used to be written out twice and had drifted — different
 * label treatment, a layout fix applied to one of them, consent recorded with
 * different fields. What actually differs is the entrypoint and what submit
 * does, so that's all the caller supplies: initial values, and `onSubmit`.
 *
 * Presentational: it renders and gates, and never calls the API. Loading the
 * consent copy and writing the answer stay with the caller, which owns the
 * consent service (`consent` is exposed for exactly that).
 */

import { h } from "../lib/jsx";
import type { ConsentPanel } from "./consent";
import { consentPanel } from "./consent";
import { CONSENT_PRIVACY_URL } from "../api/consent.copy";
import { field, gatedButton } from "./form";

export interface RegisterFormOptions {
  /** The verified email, shown locked. The OTP flow only learns it at the
   * code step, so it can be left out here and supplied via setEmail(). */
  email?: string;
  /** Prefill — the social pipeline passes the provider's names. */
  firstName?: string;
  lastName?: string;
  /** CTA label; defaults to the design copy. Variant-driven. */
  createCta?: string;
  /** Render the marketing-consent panel. Default true; checkout hides it. */
  showConsent?: boolean;
  privacyPolicyUrl?: string;
  onSubmit(e: Event): void;
}

export interface RegisterForm {
  el: HTMLFormElement;
  /** The consent panel, or null when this form hides it. */
  consent: ConsentPanel | null;
  /** The names as typed, trimmed. */
  values(): { firstName: string; lastName: string };
  setEmail(email: string): void;
  /** Re-apply the CTA gate. Needed on (re-)entry: clearBusy re-enables every
   * control in its container, the button included. */
  refreshCta(): void;
}

export function registerForm(opts: RegisterFormOptions): RegisterForm {
  // Locked to the verified identity: `readonly` rather than `disabled`, so the
  // busy-state toggle can't re-enable it, plus tabindex -1 to keep it out of
  // the tab order. The disabled look comes from `.otp-input:read-only`.
  const emailField = field("otp-reg-email", "Email", {
    name: "reg_email",
    type: "email",
    value: opts.email ?? "",
    readonly: true,
    tabindex: -1,
    placeholder: "Email",
  });
  const firstField = field("otp-first", "First name", {
    name: "first_name",
    autocomplete: "given-name",
    placeholder: "First name",
    value: opts.firstName ?? "",
  });
  const lastField = field("otp-last", "Last name", {
    name: "last_name",
    autocomplete: "family-name",
    placeholder: "Last name",
    value: opts.lastName ?? "",
  });

  const consent = opts.showConsent === false
    ? null
    : consentPanel({ privacyPolicyUrl: opts.privacyPolicyUrl ?? CONSENT_PRIVACY_URL });

  // Both names are required, so the CTA stays disabled until they're filled.
  const createBtn = gatedButton({
    label: opts.createCta ?? "Create your account",
    ready: () => {
      const { firstName, lastName } = values();
      return !!firstName && !!lastName;
    },
    watch: [firstField.input, lastField.input],
  });

  function values(): { firstName: string; lastName: string } {
    return {
      firstName: firstField.input.value.trim(),
      lastName: lastField.input.value.trim(),
    };
  }

  const el = (
    <form novalidate onSubmit={opts.onSubmit}>
      {emailField.nodes}
      <div class="otp-row">
        <div>{firstField.nodes}</div>
        <div>{lastField.nodes}</div>
      </div>
      {consent?.el}
      {createBtn.el}
    </form>
  ) as HTMLFormElement;

  return {
    el,
    consent,
    values,
    refreshCta: createBtn.refresh,
    setEmail(email) {
      emailField.input.value = email;
    },
  };
}
